import { useState, useCallback, useEffect, useRef } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { auth } from '@/config/firebase';
import { useLocalization } from '@/hooks/useLocalization';
import { signOutUser } from '@/services/auth/authService';
import { deleteUserAccount } from '@/services/api/profileApi';
import { analyticsService } from '@/services/analytics/analyticsService';
import { AppHeader } from '@/components/layout/AppHeader';
import { AppFooter } from '@/components/layout/AppFooter';
import { ROUTES } from '@/app/router/routes';
import styles from './DuplicateAccountPage.module.css';

/**
 * DuplicateAccountPage — shown when HTTP interceptor receives 409.
 *
 * Mirrors Flutter DuplicateAccountPage / DuplicateAccount widget.
 *
 * Shows:
 *   - Title: "Duplicate Account Alert"
 *   - Explanation about duplicate account
 *   - Warning that action is not reversible
 *   - "Back to login" button
 *   - "Delete Account" button with confirmation dialog
 *
 * The header is locked (WebPageHeader(isDuplicateAccount: true)). After the account is
 * deleted, the "Your Account has been deleted." dialog closes after 4 s (or with its ✕),
 * then WebProfileEvent.onLogout runs and the page reloads on /home.
 *
 * Flutter source files:
 *   - presentation/duplicate_account/duplicate_account_alert.dart
 *   - domain/core/constants/string_constants.dart (DuplicateAccountConstants)
 */

export function DuplicateAccountPage() {
  const navigate = useNavigate();
  const { t } = useLocalization();

  const [isLoading, setIsLoading] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showDeletedDialog, setShowDeletedDialog] = useState(false);

  const currentEmail = auth.currentUser?.email ?? '';

  const handleDeleteAccount = useCallback(async () => {
    setShowDeleteDialog(false);
    setIsLoading(true);

    try {
      const deleted = await deleteUserAccount();
      setIsLoading(false);
      // Flutter shows nothing when the deletion fails
      if (deleted) setShowDeletedDialog(true);
    } catch {
      setIsLoading(false);
    }
  }, []);

  /**
   * The deleted dialog closed (✕ or after 4 s) — Flutter WebProfileEvent.onLogout: logout,
   * 1 s pause, drop the job referral data, log profile_menu_options / logout, then
   * navigateTo(homeRoute) and window.location.reload().
   */
  const loggingOut = useRef(false);
  const handleDeletedDialogClose = useCallback(async () => {
    if (loggingOut.current) return;
    loggingOut.current = true;
    setShowDeletedDialog(false);
    setIsLoading(true);

    await signOutUser().catch(() => {/* continue — the account is gone */});
    await new Promise((resolve) => setTimeout(resolve, 1000));
    void analyticsService.logEvent('profile_menu_options', 'logout');
    void analyticsService.logScreenView({ screenName: ROUTES.HOME });
    // A full load of /home, like Flutter's navigate + reload
    window.location.assign(ROUTES.HOME);
  }, []);

  useEffect(() => {
    if (!showDeletedDialog) return;
    const timer = setTimeout(handleDeletedDialogClose, 4000);
    return () => clearTimeout(timer);
  }, [showDeletedDialog, handleDeletedDialogClose]);

  // If no user is logged in, redirect to login
  // (not while the deletion's logout runs — that ends on /home)
  if (!auth.currentUser && !loggingOut.current) {
    return <Navigate to={ROUTES.LOGIN} replace />;
  }

  return (
    <div className={styles.pageContainer}>
      <AppHeader isDuplicateAccount />

      <div className={styles.contentWrapper}>
        <div className={styles.content}>
          <h1 className={styles.title}>
            {t('Duplicate Account Alert')}
          </h1>

          <p className={styles.description}>
            {t('Please note that this account with user id')}{' '}
            <strong>{currentEmail}</strong>{' '}
            {t('is identified as a duplicate registered with the same mobile number as given in another account and has been disabled.')}
          </p>

          <p className={styles.description}>
            {t('You will not be able to login using this id anymore and application if any submitted using this account will be cancelled.')}
          </p>

          <p className={styles.description}>
            {t('If you wish to delete this account, please click the button provided for deletion.')}
          </p>

          <div className={styles.warningRow}>
            <span className={styles.warningIcon}>ⓘ</span>
            <span className={styles.warningNote}>{t('Note: ')}</span>
            <span className={styles.warningText}>{t('This action is not reversible')}</span>
          </div>

          <div className={styles.buttonRow}>
            <button
              type="button"
              className={styles.outlineBtn}
              onClick={() => {
                void analyticsService.logScreenView({ screenName: ROUTES.LOGIN });
                navigate(ROUTES.LOGIN, { replace: true });
              }}
            >
              {t('Back to login')}
            </button>

            <button
              type="button"
              className={styles.deleteBtn}
              onClick={() => setShowDeleteDialog(true)}
              disabled={isLoading}
            >
              {t('Delete Account')}
            </button>
          </div>
        </div>

        {isLoading && (
          <div className={styles.loadingOverlay}>
            <div className={styles.spinner} />
          </div>
        )}
      </div>

      <AppFooter />

      {/* Delete confirmation dialog */}
      {showDeleteDialog && (
        <div
          className={styles.dialogOverlay}
          onClick={() => setShowDeleteDialog(false)}
        >
          <div
            className={styles.dialogContent}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className={styles.dialogTitle}>{t('Delete My Account')}</h2>
            <p className={styles.dialogMessage}>
              {t('Are you sure want to delete your account?')}
            </p>
            <p className={styles.dialogMessage}>
              {t('Please note, this action is not reversible.')}
            </p>
            <div className={styles.dialogActions}>
              <button
                type="button"
                className={styles.dialogCancel}
                onClick={() => setShowDeleteDialog(false)}
              >
                {t('Cancel')}
              </button>
              <button
                type="button"
                className={styles.dialogDelete}
                onClick={handleDeleteAccount}
              >
                {t('Delete')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Account deleted confirmation dialog */}
      {showDeletedDialog && (
        <div className={styles.dialogOverlay}>
          <div className={styles.dialogContent} role="alertdialog" aria-modal="true">
            <button
              type="button"
              className={styles.dialogClose}
              onClick={handleDeletedDialogClose}
              aria-label="Close"
            >
              ✕
            </button>
            <div className={styles.successIcon}>✓</div>
            <p className={styles.dialogMessage}>
              {t('Your Account has been deleted.')}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
